import { removeAccents } from "../helper";

export const validatedColumns = (mandatoryColumns: string[], columns: string[]): boolean => mandatoryColumns.every((c) => columns.includes(c));

export const normalizeString = (s: string): string => removeAccents(s.trim()).split(' ').join('_').toLowerCase().replace(/[^\w\s]/g, "");

export const normalizeColumnns = (columns: string[]): string[] => columns.map((c) => removeAccents(c.trim()).split(' ').join('_'));

export const getMissingFields= (mandatoryColumns: string[], columns: string[]): string[] => mandatoryColumns.filter((c) => !columns.includes(c));

const CSV_COLUMN_KEY_ALIASES: Record<string, string> = {
    date_de_naissance: 'date_naiss',
    lieu_de_naissance: 'lieu',
    session: 'annee_obtention',
    programme: 'program_etude',
    diplome: 'grade',
    label_signataire_1: 'signataire_1_label',
    signataire_1: 'first_signataire',
    label_signataire_2: 'signataire_2_label',
    signataire_2: 'second_signataire',
    date_du_conseil: 'conseil_date',
    intitule_these: 'intitule_diplome',
};

const INTERNAL_TO_EXPECTED_CSV_COLUMN: Record<string, string> = {
    date_naiss: 'date_de_naissance',
    lieu: 'lieu_de_naissance',
    annee_obtention: 'session',
    program_etude: 'programme',
    grade: 'diplome',
    signataire_1_label: 'label_signataire_1',
    first_signataire: 'signataire_1',
    signataire_2_label: 'label_signataire_2',
    second_signataire: 'signataire_2',
    conseil_date: 'date_du_conseil',
    intitule_diplome: 'intitule_these',
};

export const normalizeCsvColumnKey = (column: string): string => {
    const normalized = normalizeString(column);
    return CSV_COLUMN_KEY_ALIASES[normalized] || normalized;
}

export const getExpectedCsvColumnName = (internalKey: string): string =>
    INTERNAL_TO_EXPECTED_CSV_COLUMN[internalKey] || internalKey;

export const normalizeCsvDataKeys = (data: Record<string, string>): Record<string, string> =>
    Object.entries(data).reduce((acc, curr) => ({...acc, [normalizeCsvColumnKey(curr[0])]: curr[1]}), {})


const dateWrongFormat = /^3(?!.*\/)/;

const normalizeInstitutionCode = (value: string): string => removeAccents((value || '').trim()).toUpperCase();

const resolveInstitutionFromOrganizationName = (organizationName: string): string => {
    if (!organizationName) {
        return '';
    }

    const [institutionCode] = organizationName.split('-', 1);
    return normalizeInstitutionCode(institutionCode || '');
}

interface ISanitizeOptions {
    organizationName?: string;
}

export const sanitizeData = (data: Record<string, string>, options?: ISanitizeOptions): Record<string, string> => {
    data['date_naiss'] = (data['date_naiss'] || '').trim();
    if (data['date_naiss'] && data['date_naiss'].split('-').length > 1) {
        data['date_naiss'] = formatDateNaiss(data['date_naiss']);
    }

    if (dateWrongFormat.test(data['date_naiss'])) {
        data['wrong_date_naiss'] = 'true';
    }

    // Garder les accents pour les noms/prénoms (affichage & impression)
    data['nom'] = (data['nom'] || "").trim();
    data['prenoms'] = (data['prenoms'] || '').trim();
    data['matricule'] = (data['matricule'] || '').replaceAll(/[^a-zA-Z0-9 ]/g, '');
    const institutionFromCsv = normalizeInstitutionCode(data['institution'] || '');
    const resolvedInstitution = resolveInstitutionFromOrganizationName(options?.organizationName || '');

    data['institution_from_csv'] = institutionFromCsv;
    data['is_institution_match'] = (!institutionFromCsv || !resolvedInstitution || institutionFromCsv === resolvedInstitution) ? 'true' : 'false';
    data['institution'] = resolvedInstitution || institutionFromCsv;
    data['ine'] = (data['ine'] || '').replaceAll(/[^a-zA-Z0-9 ]/g, '');

    return Object.entries(data).filter(([key, _]) => key && key.trim() !== '' && key.trim().length > 2).reduce((acc, curr) => ({...acc, [curr[0]]: curr[1]}), {});
}

const formatDateNaiss = (date: string): string => {
    const temp = date.split('-').map(d => d.trim());
    while (temp.length < 3) {
        temp.push('00');
    }
    
    return temp[2]+'/'+temp[1]+'/'+temp[0];
}